import { EditablePreviewWorld } from '../types'
import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import { GOAL_SUCCESS_SFX } from './missionSfx'
import { findIdByName } from './sharedHelpers'
import { BehaviorContext, BehaviorHandler, MissionBehaviors } from './types'

/**
 * RJF04（M4 コース）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF04.rbxl）のギミックを移植する:
 * - 点滅ブロック（フォルダ 2/3/5 の 20×1×20 床、赤=2 秒・黄=3 秒・緑=5 秒）:
 *   Lua の Script が CanCollide と Transparency を周期トグルする
 *   （現れている間は乗れて、消えている間はすり抜けて落ちる）。
 *   ブロックごとに初期位相（最初に見えているか）が異なる。
 * - Goal（白いゲート板 20×17×5 × 2 枚）: 接触で Winner 音 + 自壊。
 * - otasuke（緑の柱 4×10×8）: 接触で WalkSpeed=50（加速側）/ 16（リセット側）。
 *
 * プレビューでの近似・意図的な差異:
 * - Goal 音は実音源（public/sounds/）を鳴らす（missionSfx.ts に定義）。
 * - 点滅の周期は Lua の while ループ 1 周（wait(N) × 2 + 末尾の引数なし wait()）を
 *   そのまま再現する。学習者プログラムの「ずっと」も Lua generator が末尾へ
 *   wait() を入れる（インタプリタの luaWait）ため、こちらだけ末尾 wait() を
 *   省くと 1 周あたり約 1/30 秒ぶん緑ブロックが速くなり、同じ 5 秒周期を
 *   組んだ白ブロックと少しずつずれていく。
 */

/** 点滅ブロックの寸法（フォルダ 2/3/5 の床。識別に使う）。 */
const BLINK_BLOCK_SIZE = { x: 20, y: 1, z: 20 }

/** 点滅ブロックの識別の座標許容差（studs）。 */
const BLINK_MATCH_TOLERANCE = 0.5

/** 点滅ブロック 1 枚の定義（rbxl 実測。x はすべて 0）。 */
interface BlinkBlockSpec {
  readonly z: number
  readonly periodSeconds: number
  readonly startVisible: boolean
}

/**
 * 点滅ブロックの一覧（rbxl のフォルダ名 = 周期秒、Script の先頭代入 = 初期位相）。
 * 名前は再変換で連番が変わり得るため、位置（x=0 の z 座標）で識別する。
 */
const BLINK_BLOCKS: ReadonlyArray<BlinkBlockSpec> = [
  // 緑（フォルダ 5）: 5 秒周期。
  { z: -61, periodSeconds: 5, startVisible: true },
  { z: -86, periodSeconds: 5, startVisible: false },
  { z: -111, periodSeconds: 5, startVisible: true },
  // 黄（フォルダ 3）: 3 秒周期。
  { z: -161, periodSeconds: 3, startVisible: true },
  { z: -186, periodSeconds: 3, startVisible: false },
  { z: -211, periodSeconds: 3, startVisible: true },
  // 赤（フォルダ 2）: 2 秒周期。
  { z: -236, periodSeconds: 2, startVisible: false },
  { z: -261, periodSeconds: 2, startVisible: true },
  { z: -286, periodSeconds: 2, startVisible: false },
  { z: -311, periodSeconds: 2, startVisible: true },
  { z: -380, periodSeconds: 2, startVisible: false },
  { z: -405, periodSeconds: 2, startVisible: true },
  { z: -430, periodSeconds: 2, startVisible: false },
  { z: -455, periodSeconds: 2, startVisible: true },
  { z: -480, periodSeconds: 2, startVisible: false },
  { z: -505, periodSeconds: 2, startVisible: true },
  { z: -530, periodSeconds: 2, startVisible: false },
  { z: -555, periodSeconds: 2, startVisible: true },
]

/**
 * ゴール板（Winner + 自壊）のオブジェクト名。rbxl は同名 Part の連番で
 * 出力されるため、再変換で連番が変わったらここを更新する（rjf06 と同方針）。
 */
const GOAL_NAMES: ReadonlyArray<string> = ['Part#43', 'Part#44']

/** お助けパッド（WalkSpeed=50）のオブジェクト名。 */
const SPEED_UP_NAMES: ReadonlyArray<string> = [
  'Part#62',
  'Part#64',
  'Part#67',
  'Part#68',
]

/** 速度リセットパッド（WalkSpeed=16）のオブジェクト名。 */
const SPEED_RESET_NAMES: ReadonlyArray<string> = [
  'Part#63',
  'Part#65',
  'Part#66',
]

/** お助けパッドの歩行速度（Lua: humanoid.WalkSpeed = 50 / 16）。 */
const SPEED_UP_WALK_SPEED = 50
const SPEED_RESET_WALK_SPEED = 16

/** Goal 音。実機の Workspace.Winner に対応する実音源を鳴らす。 */
const playWinnerSound = (signal: AbortSignal): void => {
  playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, signal)
}

/** 位置テーブルから点滅ブロックのオブジェクト ID を解決する。 */
const findBlinkBlockId = (
  world: EditablePreviewWorld,
  spec: BlinkBlockSpec,
): string | undefined => {
  for (const state of world.getState().values()) {
    if (
      Math.abs(state.size.x - BLINK_BLOCK_SIZE.x) <= BLINK_MATCH_TOLERANCE &&
      Math.abs(state.size.y - BLINK_BLOCK_SIZE.y) <= BLINK_MATCH_TOLERANCE &&
      Math.abs(state.size.z - BLINK_BLOCK_SIZE.z) <= BLINK_MATCH_TOLERANCE &&
      Math.abs(state.position.x) <= BLINK_MATCH_TOLERANCE &&
      Math.abs(state.position.z - spec.z) <= BLINK_MATCH_TOLERANCE
    ) {
      return state.id
    }
  }
  return undefined
}

/** 点滅ブロック 1 枚に表示/非表示を適用する（Lua: CanCollide + Transparency）。 */
const applyBlinkState = (
  world: EditablePreviewWorld,
  objectId: string,
  visible: boolean,
): void => {
  world.setCanCollide(objectId, visible)
  world.setTransparency(objectId, visible ? 0 : 1)
}

/**
 * 1 枚の点滅ループ（Lua: 各 Part の Script）。
 *
 * Lua の 1 周をそのまま 1 イテレーションに写す:
 *   while true do
 *     <初期状態を適用>; wait(N)
 *     <反転を適用>;     wait(N)
 *     wait()            -- ループ末尾の 1 フレーム待機
 *   end
 *
 * 末尾の wait() を省くと 1 周が FRAME_SECONDS ぶん短くなる。学習者が
 * 「ずっと{あらわす→N 秒→かくす→N 秒}」と同じ周期を組んでも、インタプリタ側は
 * Lua generator と同じく「ずっと」の末尾に wait() を入れる（luaWait）ため、
 * 白ブロックだけ 1 周あたり約 1/30 秒長くなり、周回を重ねるほど相対的に
 * ずれていく（Roblox Studio では両者とも末尾 wait() を持つのでずれない）。
 */
const runBlink = async (
  ctx: BehaviorContext,
  objectId: string,
  spec: BlinkBlockSpec,
): Promise<void> => {
  const { world } = ctx
  while (!ctx.signal.aborted) {
    if (!world.exists(objectId)) {
      return
    }
    applyBlinkState(world, objectId, spec.startVisible)
    await ctx.wait(spec.periodSeconds)
    if (ctx.signal.aborted || !world.exists(objectId)) {
      return
    }
    applyBlinkState(world, objectId, !spec.startVisible)
    await ctx.wait(spec.periodSeconds)
    if (ctx.signal.aborted) {
      return
    }
    // Lua の while ループ末尾の wait()（引数なし = 約 1/30 秒）。
    await ctx.wait(FRAME_SECONDS)
  }
}

/** 全点滅ブロックのループを並行起動する。 */
const blinkBlocks: BehaviorHandler = async (ctx) => {
  const loops: Array<Promise<void>> = []
  for (const spec of BLINK_BLOCKS) {
    const objectId = findBlinkBlockId(ctx.world, spec)
    if (objectId) {
      loops.push(runBlink(ctx, objectId, spec))
    }
  }
  await Promise.all(loops)
}

export const createRjf04Behaviors = (): MissionBehaviors => {
  const onTouchCharacter = new Map<string, BehaviorHandler>()
  for (const name of GOAL_NAMES) {
    // Lua: Goal/Part/Script。接触で Winner 音を鳴らし、板を自壊させる。
    onTouchCharacter.set(name, ({ world, signal }) => {
      playWinnerSound(signal)
      const id = findIdByName(world, name)
      if (id) {
        world.destroy(id)
      }
    })
  }
  for (const name of SPEED_UP_NAMES) {
    onTouchCharacter.set(name, ({ world }) => {
      world.setAvatarWalkSpeed(SPEED_UP_WALK_SPEED)
    })
  }
  for (const name of SPEED_RESET_NAMES) {
    onTouchCharacter.set(name, ({ world }) => {
      world.setAvatarWalkSpeed(SPEED_RESET_WALK_SPEED)
    })
  }
  return {
    onPlay: [blinkBlocks],
    onTouchCharacter,
  }
}
